// enroll.js
import moment from 'moment';
import cookies from 'js-cookie';
import { select } from '../shared/utils/dom';
import { delimiters } from '../shared/constants';
import { oneTimePaymentApi, removeErrors, getErrorMessages, showSuccessErrorMessage, achPaymentApi, showError } from './api';
import { createError, removeError } from '../shared/formErrors';
import formMixin from '../shared/vue/mixins/form';
import multistepMixin from '../shared/vue/mixins/multistep';
import bankAccountMixin from '../shared/vue/mixins/bankAccount';
import dataLayerPush from '../shared/utils/analytics';
import { formatBasicCurrency } from '../shared/utils/misc';

const achThresholdCookieName = 'achValidationThresholdHasBeenExceeded';
/**
 * Enrolling in auto pay
 */
export default function oneTimePayment() {
  const el = select('[data-vue-app="one-time-payment"]');
  const paymentInsufficientWarningMessage = 'Your payment is insufficient to avoid shut-off.';

  if (el) {
    const app = new Vue({ // eslint-disable-line no-unused-vars
      el,
      delimiters,
      mixins: [bankAccountMixin, formMixin, multistepMixin],
      data: {
        account: '',
        accountType: 'Checking',
        agree: false,
        agreeACH: false,
        customAmount: '',
        hasPendingPayment: false,
        date: '',
        // for accessibility in live regions
        liveText: '',
        modalIsOpen: false,
        paymentDate: null,
        paymentDay: '',
        routingNumber: '',
        reenterAccountNumber: '',
        success: false,
        cancelSuccess: false,
        canBeCancelled: true,
        paymentAmount: '',
        receiptNumber: '',
        confirmationNumber: null,
        errorMessage: null,
        loadingExistingPayment: false,
        shutoffAmount: 0,
        onBillPromoAmount: 0,
        agreePaymentPlan: false,
        maxPaymentDate: new moment().add(30, 'days').toDate(), // eslint-disable-line new-cap
        // create an array to hold the valid payment dates
        validPaymentDates: [],
        dueDate: null,
        warningMessage: null,
        errorMessages: getErrorMessages(),
        selectedDateAfterDueDate: false,
        submitting: false,
        validationMessage: '',
        valid: false,
        achCounter: 0,
        achThresholdExceeded: false,
        initialSelectedAmount: '',
        paymentusLink: '',
        serviceaccountNumber: '',
      },
      computed: {
        showWayfinding() {
          return !this.success && !this.cancelSuccess && !this.confirmationNumber;
        },
        actualPaymentAmount() {
          return this.paymentAmount === 'custom' ? this.customAmount : this.paymentAmount;
        },
        displayDate() {
          if (this.paymentDate) {
            return moment.utc(new Date(this.paymentDate)).format('MMM D, YYYY');
          }
          return 'Current Due Date';
        },
        formattedPaymentAmount() {
          return formatBasicCurrency(this.actualPaymentAmount);
        },
        onBillPromoAmountSelected() {
          if (!this.selectedDateAfterDueDate && this.onBillPromoAmount > 0 && ((this.paymentAmount !== 'custom' && Math.abs(this.paymentAmount - this.onBillPromoAmount) === 0) || (this.paymentAmount === 'custom' && Math.abs(this.customAmount - this.onBillPromoAmount) === 0))) {
            return true;
          }
          return false;
        },
      },
      mounted() {
        // set the valid dates
        this.shutoffAmount = (this.$el.dataset && this.$el.dataset.shutoffAmount) ? this.$el.dataset.shutoffAmount : 0;
        this.validPaymentDates = (this.$el.dataset && this.$el.dataset.validDates) ? JSON.parse(this.$el.dataset.validDates) : null;
        this.paymentAmount = (this.$el.dataset) ? this.$el.dataset.initialAmount : '';
        this.dueDate = (this.$el.dataset) ? this.$el.dataset.dueDate : null;
        this.achThresholdExceeded = cookies.get(achThresholdCookieName) ? cookies.get(achThresholdCookieName) : false;
        this.onBillPromoAmount = (this.$el.dataset && this.$el.dataset.onBillPromoAmount) ? this.$el.dataset.onBillPromoAmount : 0;
        this.initialSelectedAmount = (this.$el.dataset) ? this.$el.dataset.initialAmount : ''; // for paymentus link
        this.paymentusLink = (this.$el.dataset) ? this.$el.dataset.paymentusLink : '';
        this.serviceaccountNumber = (this.$el.dataset) ? this.$el.dataset.service : '';
        const self = this;

        Vue.nextTick(() => {
          // Load any existing one-time payment
          self.loadingExistingPayment = true;
          oneTimePaymentApi.getExistingOneTimePayment()
            .then((payment) => {
              self.loading = false;
              self.loadingExistingPayment = false;

              if (payment) {
                self.confirmationNumber = payment.paymentId;
                self.routingNumber = payment.routingNumber;
                self.accountType = payment.bankAccountType;
                self.accountNumber = payment.last4Account;
                self.paymentDate = payment.postDate;
                self.paymentAmount = payment.paymentAmount;
                self.canBeCancelled = payment.canBeCancelled;
                self.hasPendingPayment = true;
                self.setStep(4);
              }
            });
        });
      },
      created() {
        Event.$on('A11yDialogConfirm:paymentus-one-time-message1', () => {
          this.getPaymentusLinkAndSubmitForm1();
        });
      },
      watch: {
        step(newVal, oldVal) { // eslint-disable-line no-unused-vars
          /**
           * Changes live text depending on the form step
           */
          if (this.step === 2) {
            this.liveText = 'Moving to Step 2 to confirm your payment details';
          } else if (this.step === 1) {
            this.liveText = 'You are now back on Step 1 to set up your one time payment';
          } else if (this.step === 3) {
            this.liveText = 'Moving to Step 3 to submit your one time payment';
          }
        },
        paymentAmount(newVal, oldVal) { // eslint-disable-line no-unused-vars
          removeErrors();
        },
        customAmount(newVal, oldVal) { // eslint-disable-line no-unused-vars
          removeErrors();
        },
        paymentDate(newVal, oldVal) { // eslint-disable-line no-unused-vars
          // if the date has been updated, set the live text to read the date
          // because Pikaday wasn't reading the date right, and we want to make
          // sure this is accessible. Also, read if the date selected is invalid
          if (newVal) {
            const formattedDate = moment(newVal, 'MMMM D, YYYY').format('MMM D, YYYY');
            const invalidDate = this.validPaymentDates ? this.validPaymentDates.indexOf(formattedDate) === -1 : false;
            if (invalidDate === true) {
              this.liveText = `${newVal} is not a valid date. Please select another date.`;
            } else {
              this.liveText = `${newVal} is the selected date`;
            }
          }

          // if there's no due date, don't worry about it
          if (!this.dueDate) {
            this.selectedDateAfterDueDate = false;
          } else if (moment(new Date(this.paymentDate)).isAfter(new Date(this.dueDate))) {
            if (oldVal !== null && oldVal !== undefined && this.onBillPromoAmountSelected) {
              this.paymentAmount = '';
            }
            this.selectedDateAfterDueDate = true;
          } else {
            this.selectedDateAfterDueDate = false;
          }
        },
      },
      /**
       * Methods
       */
      methods: {
        checkIfPaymentus() {
          if (this.account === "paymentus") {
            this.account = "";
          }
        },

        checkIfSufficientPayment() {
          // make sure to show the warning message if the actual payment amount isn't enought to avoid shutoff
          // and the due date is after today's date (a future date)
          if (this.actualPaymentAmount !== '' && this.actualPaymentAmount > 0 && this.shutoffAmount > 0 && (parseFloat(this.actualPaymentAmount) < parseFloat(this.shutoffAmount)) && moment(new Date(this.dueDate)).isAfter(moment())) {
            this.warningMessage = paymentInsufficientWarningMessage;
          } else {
            this.warningMessage = null;
          }
        },
        handleClickOnContinue(e) {
          // when the user clicks on continue button, validate there's a value in the input field. if empty, show error
          // if there are no errors, let the user proceed.
          e.preventDefault();
          let hasError = false;

          /*
          * Step 1 validation
          */

          // only validate new bank account information if it's shown
          if (this.account === 'new') {
            if (!this.routingNumber) {
              createError(this.$refs.routingNumber, this.$refs.routingNumberError, 'Please enter a routing number.');
              hasError = true;
            } else {
              removeError(this.$refs.routingNumber, this.$refs.routingNumberError);
            }

            if (!this.accountNumber) {
              createError(this.$refs.accountNumber, this.$refs.accountNumberError, 'Please enter an account number.');
              hasError = true;
            } else {
              removeError(this.$refs.accountNumber, this.$refs.accountNumberError);
            }

            if (!this.reenterAccountNumber) {
              createError(this.$refs.reenterAccountNumber, this.$refs.reenterAccountNumberError, 'Please reenter the account number.');
              hasError = true;
            } else if (this.accountNumber !== this.reenterAccountNumber) {
              createError(this.$refs.reenterAccountNumber, this.$refs.reenterAccountNumberError, 'Account numbers do not match.');
              hasError = true;
            } else {
              removeError(this.$refs.reenterAccountNumber, this.$refs.reenterAccountNumberError);
            }
          }

          // validate payment date
          if (!this.paymentDate) {
            // datepicker is a component, so we need to reference the element with $el
            createError(this.$refs.datepicker.$el, this.$refs.datepickerError, 'Please select a payment date.');
            hasError = true;
          } else if (this.validPaymentDates !== null && this.validPaymentDates.indexOf(new moment(new Date(this.paymentDate)).format('MMM D, YYYY')) === -1) { // eslint-disable-line new-cap
            createError(this.$refs.datepicker.$el, this.$refs.datepickerError, 'Please select a valid payment date.');
            hasError = true;
          } else {
            removeError(this.$refs.datepicker.$el, this.$refs.datepickerError);
          }

          // validate payment amount
          if (!this.paymentAmount) {
            createError(this.$refs.paymentAmount, this.$refs.paymentAmountError, 'Please specify an amount.');
            hasError = true;
          } else {
            removeError(this.$refs.paymentAmount, this.$refs.paymentAmountError);
          }

          // only validate the custom amount if it's shown
          if (this.paymentAmount === 'custom') {
            if (!this.customAmount) {
              createError(this.$refs.customAmount, this.$refs.customAmountError, 'Please specify an amount.');
              hasError = true;
            } else {
              removeError(this.$refs.customAmount, this.$refs.customAmountError);
            }
          }

          if (hasError) {
            // update live text to let the user know there are errors
            this.liveText = 'The form has errors. Please make sure your payment information is correct.';
          } else {
            // make sure to show the warning message on review details if the payment is insufficient to avoid shutoff
            if (this.actualPaymentAmount !== '' && this.actualPaymentAmount > 0 && this.shutoffAmount > 0 && (parseFloat(this.actualPaymentAmount) < parseFloat(this.shutoffAmount))) {
              this.warningMessage = paymentInsufficientWarningMessage;
            }
            if (this.account === 'new') {
              this.loading = true;
              this.submitting = true;
              achPaymentApi.validateAccountNumber(this, false, true)
                .then((response) => {
                  this.loading = false;
                  this.submitting = false;
                  this.validationMessage = response.data.validationMessage;
                  this.valid = response.data.valid;
                  if (!response.data.uSBankDown) {
                    if (this.valid) {
                      this.nextStep(e);
                    } else {
                      const message = { PrimaryMessage: "We're Sorry", SecondaryMessage: this.validationMessage };
                      showError("ach-payment-error", message);
                      this.achCounter += 1;
                      if (this.achCounter >= 3) {
                        this.sendAchRemark();
                        this.achThresholdExceeded = true;
                        cookies.set(achThresholdCookieName, true);
                        Event.$emit('A11yDialogOpen', { dialogKey: 'ach-validation-threshold-exceeded' });
                      }
                    }
                  } else {
                    this.nextStep(e);
                  }
                })
                .catch(() => {
                  this.loading = false;
                  this.submitting = false;
                  removeErrors();
                  showSuccessErrorMessage(this.errorMessages, "OTP-06");
                });
            } else {
              this.nextStep(e);
            }
          }
        },
        sendAchRemark() {
          this.loading = true;
          this.submitting = true;
          achPaymentApi.sendAchRemark(this, false, true)
            .then(() => {
              this.loading = false;
              this.submitting = false;
            })
            .catch(() => {
              this.loading = false;
              this.submitting = false;
              removeErrors();
              showSuccessErrorMessage(this.errorMessages, "OTP-06");
            })
        },
        confirmCancelPayment() {
          this.loading = true;
          oneTimePaymentApi.cancelPayment(this.confirmationNumber)
            .then(() => {
              this.cancelSuccess = true;
              this.loading = false;
              this.hasPendingPayment = false;
            })
            .catch(() => {
              this.cancelSuccess = false;
              this.loading = false;
              removeErrors();
              showSuccessErrorMessage(this.errorMessages, "OTP-06");
            })
        },
        postFormContents() {
          // Return right away if loading to prevent double submits.
          if (this.submitting) {
            return;
          }

          this.loading = true;
          this.submitting = true;
          oneTimePaymentApi.makePayment(this)
            .then((response) => {
              this.confirmationNumber = response.data.checkPaymentCreateResponse.eftSetupConfirmationNumber;
              this.canBeCancelled = response.data.canBeCancelled;
              this.success = true;
              this.hasPendingPayment = true;
              this.setStep(4);
              this.loading = false;
              this.submitting = false;
              dataLayerPush('PayBill');
            })
            .catch(() => {
              this.success = false;
              this.loading = false;
              this.submitting = false;
            });
        },
        getPaymentusLinkAndSubmitForm1() {
          this.loading = true;
          this.account = "";
          this.errorGeneratingToken = false;
          const queryParams = {
            amount: this.initialSelectedAmount,
          };
          oneTimePaymentApi.getPaymentToken(this.serviceaccountNumber, queryParams)
            .then((response) => {
              this.success = true;
              this.loading = false;
              location.href = response;
            })
            .catch(() => {
              this.success = false;
              this.errorGeneratingToken = true;
              this.loading = false;
            })
        },
      },
    });
  }
}



// WEBPACK FOOTER //
// ./src/assets/toolkit/scripts/portal/oneTimePayment.js